import type {
  INeoWorkflowService,
  NeoWorkflowError,
} from "../ports/driver/neoWorkflowService";
import type {
  INeoWorkflowRepository,
  InboundEmailBindingStatus,
  SimulatedEmailPayload,
  WorkflowCreatePayload,
  WorkflowEdgeInput,
  WorkflowNodeClinicScopeCheckPayload,
  WorkflowNodeInput,
  WorkflowUpdatePayload,
} from "../ports/driven/neoWorkflowRepository";

const toError = (error: unknown): NeoWorkflowError => {
  // A stale-revision conflict (thrown by the repository on HTTP 409) is a
  // recoverable signal, not a hard failure: surface it so the caller refetches
  // and reapplies instead of showing an error. Detected via a duck-typed
  // marker to avoid importing the adapter class across the hexagon boundary.
  if (
    error &&
    typeof error === "object" &&
    (error as { graphRevisionConflict?: unknown }).graphRevisionConflict ===
      true
  ) {
    return {
      message: "The workflow graph changed since it was loaded.",
      conflict: true,
      currentRevision:
        (error as { currentRevision?: number | null }).currentRevision ?? null,
    };
  }
  return {
    message: error instanceof Error ? error.message : "Something went wrong.",
  };
};

export const NeoWorkflowServiceImplFactory = ({
  neoWorkflowRepository,
}: {
  neoWorkflowRepository: INeoWorkflowRepository;
}): INeoWorkflowService => {
  const listWorkflows: INeoWorkflowService["listWorkflows"] = async () => {
    try {
      const resp = await neoWorkflowRepository.listWorkflowsAPI();
      return { success: true, data: resp?.items ?? [] };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const createWorkflow = async (payload: WorkflowCreatePayload) => {
    try {
      const data = await neoWorkflowRepository.createWorkflowAPI(payload);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const getWorkflowGraph = async (workflowId: number) => {
    try {
      const data = await neoWorkflowRepository.getWorkflowGraphAPI(workflowId);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const replaceWorkflowGraph = async (
    workflowId: number,
    nodes: WorkflowNodeInput[],
    edges: WorkflowEdgeInput[],
    baseRevision?: number | null
  ) => {
    try {
      const data = await neoWorkflowRepository.replaceWorkflowGraphAPI(
        workflowId,
        nodes,
        edges,
        baseRevision
      );
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const generateNodeOutputSchema = async (
    workflowId: number,
    nodeId: number
  ) => {
    try {
      const data = await neoWorkflowRepository.generateNodeOutputSchemaAPI(
        workflowId,
        nodeId
      );
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const restoreLastRunGraph = async (workflowId: number) => {
    try {
      const data = await neoWorkflowRepository.restoreLastRunGraphAPI(
        workflowId
      );
      if (
        !data ||
        typeof (data as { workflow?: unknown }).workflow !== "object"
      ) {
        const detail = (data as { detail?: unknown } | null)?.detail;
        return {
          success: false,
          error: {
            message:
              typeof detail === "string"
                ? detail
                : "Could not restore the last-run version.",
          },
        };
      }
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const keepCurrentGraphVersion = async (workflowId: number) => {
    try {
      const data = await neoWorkflowRepository.keepCurrentGraphVersionAPI(
        workflowId
      );
      if (
        !data ||
        typeof (data as { workflow?: unknown }).workflow !== "object"
      ) {
        const detail = (data as { detail?: unknown } | null)?.detail;
        return {
          success: false,
          error: {
            message:
              typeof detail === "string"
                ? detail
                : "Could not keep the current workflow version.",
          },
        };
      }
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const checkNodeClinicScope = async (
    workflowId: number,
    nodeId: number,
    payload?: WorkflowNodeClinicScopeCheckPayload
  ) => {
    try {
      const data = await neoWorkflowRepository.checkNodeClinicScopeAPI(
        workflowId,
        nodeId,
        payload
      );
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const updateWorkflow = async (
    workflowId: number,
    payload: WorkflowUpdatePayload
  ) => {
    try {
      const data = await neoWorkflowRepository.updateWorkflowAPI(
        workflowId,
        payload
      );
      // The shared HTTP transport resolves a FAILED PATCH with the error
      // *body* (e.g. FastAPI's `{ detail }`) rather than throwing (see the
      // PATCH catch in apiProvider). A real workflow always carries a numeric
      // `id`; if it doesn't, the update actually failed (e.g. a 404 from the
      // owner-gate, a 400 bad cron) — report it as an error so callers do NOT
      // `setMeta` the error body and blank the workflow. Symptom this prevents:
      // a rejected schedule save wiping `schedule_type` and flipping the
      // trigger UI to "Manually".
      if (!data || typeof (data as { id?: unknown }).id !== "number") {
        const detail = (data as { detail?: unknown } | null)?.detail;
        // The update route conflates "not owned" and "not found" into a 404
        // with detail "workflow not found" (so it can't leak existence to a
        // non-owner). Since the workflow was loadable, a failed edit is an
        // ownership refusal in practice — surface a permission message rather
        // than the misleading "not found". Other details (e.g. a 400 bad-cron
        // message) are passed through unchanged.
        const message =
          detail === "workflow not found"
            ? "You do not have permission to edit this workflow."
            : typeof detail === "string"
            ? detail
            : "Could not update the workflow.";
        return { success: false, error: { message } };
      }
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const deleteWorkflow = async (workflowId: number) => {
    try {
      await neoWorkflowRepository.deleteWorkflowAPI(workflowId);
      return { success: true, data: null };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const runWorkflow = async (
    workflowId: number,
    options?: {
      inputFileIds?: number[];
      simulatedEmail?: SimulatedEmailPayload;
      clinicId?: number;
    }
  ) => {
    try {
      const data = await neoWorkflowRepository.runWorkflowAPI(workflowId, {
        input_file_ids: options?.inputFileIds,
        simulated_email: options?.simulatedEmail,
        clinic_id: options?.clinicId,
      });
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const getWorkflowRunProgress = async (runId: string) => {
    try {
      const data = await neoWorkflowRepository.getWorkflowRunProgressAPI(runId);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const listWorkflowRuns = async (workflowId: number, limit?: number) => {
    try {
      const resp = await neoWorkflowRepository.listWorkflowRunsAPI(
        workflowId,
        limit
      );
      return { success: true, data: resp?.items ?? [] };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const listWorkflowRunConversations = async (runId: string) => {
    try {
      const resp = await neoWorkflowRepository.listWorkflowRunConversationsAPI(
        runId
      );
      return { success: true, data: resp?.items ?? [] };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const listWorkflowEligibleClinics = async (workflowId: number) => {
    try {
      const resp = await neoWorkflowRepository.listWorkflowEligibleClinicsAPI(
        workflowId
      );
      return { success: true, data: resp?.items ?? [] };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const listWorkflowEmailAddresses = async (workflowId: number) => {
    try {
      const resp = await neoWorkflowRepository.listWorkflowEmailAddressesAPI(
        workflowId
      );
      return { success: true, data: resp?.items ?? [] };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const listWorkflowEmailBindings = async (workflowId: number) => {
    try {
      const resp = await neoWorkflowRepository.listWorkflowEmailBindingsAPI(
        workflowId
      );
      return { success: true, data: resp?.items ?? [] };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const createWorkflowEmailBinding = async (
    workflowId: number,
    addressId: number,
    clinicId: number,
    options?: {
      condition?: Record<string, unknown> | null;
      senderAllowlist?: string | null;
    }
  ) => {
    try {
      const data = await neoWorkflowRepository.createWorkflowEmailBindingAPI(
        workflowId,
        {
          address_id: addressId,
          clinic_id: clinicId,
          condition: options?.condition ?? null,
          sender_allowlist: options?.senderAllowlist ?? undefined,
        }
      );
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const setWorkflowEmailBindingStatus = async (
    workflowId: number,
    bindingId: number,
    status: InboundEmailBindingStatus
  ) => {
    try {
      const data = await neoWorkflowRepository.patchWorkflowEmailBindingAPI(
        workflowId,
        bindingId,
        { status }
      );
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const setWorkflowEmailBindingSenderAllowlist = async (
    workflowId: number,
    bindingId: number,
    senderAllowlist: string
  ) => {
    try {
      const data = await neoWorkflowRepository.patchWorkflowEmailBindingAPI(
        workflowId,
        bindingId,
        { sender_allowlist: senderAllowlist }
      );
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const setWorkflowEmailBindingClinic = async (
    workflowId: number,
    bindingId: number,
    clinicId: number
  ) => {
    try {
      const data = await neoWorkflowRepository.patchWorkflowEmailBindingAPI(
        workflowId,
        bindingId,
        { clinic_id: clinicId }
      );
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const deleteWorkflowEmailBinding = async (
    workflowId: number,
    bindingId: number
  ) => {
    try {
      await neoWorkflowRepository.deleteWorkflowEmailBindingAPI(
        workflowId,
        bindingId
      );
      return { success: true, data: null };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  return {
    listWorkflows,
    createWorkflow,
    getWorkflowGraph,
    replaceWorkflowGraph,
    restoreLastRunGraph,
    keepCurrentGraphVersion,
    generateNodeOutputSchema,
    checkNodeClinicScope,
    updateWorkflow,
    deleteWorkflow,
    runWorkflow,
    getWorkflowRunProgress,
    listWorkflowRuns,
    listWorkflowRunConversations,
    listWorkflowEligibleClinics,
    listWorkflowEmailAddresses,
    listWorkflowEmailBindings,
    createWorkflowEmailBinding,
    setWorkflowEmailBindingStatus,
    setWorkflowEmailBindingSenderAllowlist,
    setWorkflowEmailBindingClinic,
    deleteWorkflowEmailBinding,
  };
};
